import _ from 'lodash';

import {
  firebaseAddDoc,
  firebaseDeleteDoc,
  firebaseGetById,
  firebaseGetAll,
  firebaseGetBy,
  firebaseUpdateDoc,
  firebaseGetDocRef,
} from '@client/api/firebase.api';
import {
  cleanData,
  createMetadata,
  dayjsFromTimestamp,
  dayjsToTimestamp,
  getAuthUser,
  updateMetadata,
} from '@client/services/common.service';
import { logger } from '@client/utils/logger.util';
import {
  CScheduler,
  ESort,
  EStatus,
  type IScheduler,
  type TSchedulerEntityPaths,
  type ESchedulerPrefix,
  type IObjectId,
  type TSchedulers,
  EEndReasonType,
} from '@common/types';

import type { TPaginateProps } from '@client/types/antd.type';
import type { TablePaginationConfig } from 'antd/es/table/interface';
import type { Dayjs } from 'dayjs';
import type { User } from 'firebase/auth';
import type { DocumentData, Timestamp } from 'firebase/firestore';

export const schedulerPath = 'schedulers';

// Prevent infinite re-renders by only updating the state when values actually change.
export const areSameScheduler = (a: any[] = [], b: any[] = []) => {
  if (a === b) return true;
  const al = Array.isArray(a) ? a.length : 0;
  const bl = Array.isArray(b) ? b.length : 0;
  if (al !== bl) return false;
  for (let i = 0; i < al; i++) {
    const av = (a[i] && (a[i].id ?? a[i])) as any;
    const bv = (b[i] && (b[i].id ?? b[i])) as any;
    if (av !== bv) return false;
  }
  return true;
};

export const schedulerDatesToTs = (scheduler: IScheduler) => ({
  ...scheduler,
  range: {
    ...scheduler.range,
    endReason: {
      ...scheduler.range.endReason,
      expiredAt:
        scheduler.range.endReason.type === EEndReasonType.FOREVER
          ? null
          : dayjsToTimestamp(scheduler.range.endReason.expiredAt as Dayjs),
    },
    startedAt: dayjsToTimestamp(scheduler.range.startedAt as Dayjs),
  },
});

export const schedulerDatesToDayjs = (scheduler: IScheduler) => ({
  ...scheduler,
  range: {
    ...scheduler.range,
    endReason: {
      ...scheduler.range.endReason,
      expiredAt:
        scheduler.range.endReason.type === EEndReasonType.FOREVER
          ? null
          : dayjsFromTimestamp(
              scheduler.range.endReason.expiredAt as Timestamp,
            ),
    },
    startedAt: dayjsFromTimestamp(scheduler.range.startedAt as Timestamp),
  },
});

type TCreateSchedulers<T extends IObjectId> = {
  entityId: T['id'];
  schedulers: TSchedulers;
  onCreate: <T extends IObjectId>(
    entityId: T['id'],
    data: IScheduler,
  ) => Promise<void>;
};

export const createSchedulers = async <T extends IObjectId>(
  props: TCreateSchedulers<T>,
) => {
  const { entityId, schedulers, onCreate } = props;

  for (const prefix in schedulers[CScheduler]) {
    if (Object.prototype.hasOwnProperty.call(schedulers[CScheduler], prefix)) {
      const schedulersList = schedulers[CScheduler][prefix as ESchedulerPrefix];
      if (schedulersList.length) {
        await Promise.all(
          schedulersList.map(async (scheduler: IScheduler) => {
            await onCreate<T>(entityId, scheduler);
          }),
        );
      }
    }
  }
};

type TSyncSchedulers<T extends IObjectId> = {
  entityId: T['id'];
  schedulers: TSchedulers;
  onCreate: <T extends IObjectId>(
    entityId: T['id'],
    data: IScheduler,
  ) => Promise<void>;
  onUpdate: (id: Required<IScheduler['id']>, data: IScheduler) => Promise<void>;
};

export const syncSchedulers = async <T extends IObjectId>(
  props: TSyncSchedulers<T>,
) => {
  const { entityId, schedulers, onCreate, onUpdate } = props;

  for (const prefix in schedulers[CScheduler]) {
    if (Object.prototype.hasOwnProperty.call(schedulers[CScheduler], prefix)) {
      const schedulersList = schedulers[CScheduler][prefix as ESchedulerPrefix];
      if (schedulersList.length) {
        await Promise.all(
          schedulersList.map(async (scheduler: IScheduler) => {
            if (scheduler.id && !scheduler.id?.startsWith('new-')) {
              // Scheduler exists, update it
              await onUpdate(scheduler.id, scheduler);
            } else {
              // New scheduler, create it
              await onCreate<T>(entityId, scheduler);
            }
          }),
        );
      }
    }
  }
};

export const createScheduler = async <T extends IObjectId>(
  data: IScheduler,
  entityId: T['id'],
  entityPath: TSchedulerEntityPaths,
) => {
  try {
    const user = getAuthUser();

    const entityRef = firebaseGetDocRef({
      collectionPath: entityPath,
      document: entityId,
    });

    return firebaseAddDoc<IScheduler>({
      collectionPath: schedulerPath,
      data: {
        ...cleanData<IScheduler>(data),
        entityRef,
        status: EStatus.ACTIVE,
        metadata: createMetadata(user),
      },
    });
  } catch (error) {
    logger.error('createScheduler', error);
    throw error;
  }
};

export const getScheduler = async (
  id: Required<IScheduler['id']>,
): Promise<IScheduler | null> => {
  try {
    const scheduler = await firebaseGetById({
      collectionPath: schedulerPath,
      document: id as string,
    });
    return scheduler as IScheduler;
  } catch (error) {
    logger.error('getScheduler', error);
    throw error;
  }
};

export const updateScheduler = async (
  id: Required<IScheduler['id']>,
  data: IScheduler,
) => {
  try {
    const user = getAuthUser();

    const existing = await firebaseGetById({
      collectionPath: schedulerPath,
      document: id,
    });

    let existingMetadata = {};
    let existingEntityRef = null;
    if (existing) {
      existingMetadata = existing.metadata ?? {};
      existingEntityRef = (existing as any).entityRef ?? null;
    }

    // Ensure date fields are stored as Firestore Timestamps
    const dataWithTimestamps = schedulerDatesToTs(data);

    // Remove entityRef from data before cleaning to avoid serializing Firebase internals
    const { entityRef: _entityRef, ...dataWithoutEntityRef } =
      dataWithTimestamps as any;

    return firebaseUpdateDoc({
      collectionPath: schedulerPath,
      document: id as string,
      data: {
        ...cleanData(dataWithoutEntityRef),
        // Preserve the existing entityRef from Firestore (it's already a DocumentReference)
        entityRef: existingEntityRef,
        metadata: {
          ...existingMetadata,
          ...updateMetadata(user as User),
        },
      },
    });
  } catch (error) {
    logger.error('updateScheduler', error);
    throw error;
  }
};

export const deleteScheduler = async (id: Required<IScheduler['id']>) =>
  firebaseDeleteDoc({
    collectionPath: schedulerPath,
    document: id as string,
  });

export const deleteEntitySchedulers = async <T extends IObjectId>(
  entityId: T['id'],
  entityPath: TSchedulerEntityPaths,
) => {
  const user = getAuthUser();

  try {
    const entityRef = firebaseGetDocRef({
      collectionPath: entityPath,
      document: entityId,
    });

    const schedulers =
      (await firebaseGetBy<IScheduler & { id: string }>({
        collectionPath: schedulerPath,
        wheres: [
          { field: 'entityRef', value: entityRef },
          { field: 'metadata.createdBy.uid', value: user.uid },
        ],
      })) || [];

    const deletePromises = schedulers.map((doc) =>
      firebaseDeleteDoc({
        collectionPath: schedulerPath,
        document: doc.id as string,
      }),
    );

    await Promise.all(deletePromises);

    return { success: true, count: deletePromises.length };
  } catch (error) {
    logger.error('deleteEntitySchedulers error', error);
    throw error;
  }
};

export const getAllSchedulers = async (
  params?: TPaginateProps & TablePaginationConfig,
) => {
  logger.debug('params', params);

  try {
    const schedulers = await firebaseGetAll<IScheduler & { id: string }>({
      collectionPath: schedulerPath,
    });

    const data: IScheduler[] = [];
    if ('length' in schedulers) {
      schedulers?.forEach((doc: IScheduler & { id: string }) => {
        data.push(_.merge(doc, { id: doc.id }) as IScheduler);
      });
    }

    return data;
  } catch (error) {
    logger.error('getSchedulers', error);
    throw error;
  }
};

export const getSchedulers = async <T extends IObjectId>(
  entityId: T['id'],
  entityPath: TSchedulerEntityPaths,
  schedulerType: ESchedulerPrefix,
  params?: TPaginateProps & TablePaginationConfig,
) => {
  const user = getAuthUser();

  try {
    const entityRef = firebaseGetDocRef({
      collectionPath: entityPath,
      document: entityId,
    });

    const result =
      (await firebaseGetBy({
        collectionPath: schedulerPath,
        wheres: [
          { field: 'entityRef', value: entityRef },
          { field: 'type', value: schedulerType },
          { field: 'metadata.createdBy.uid', value: user.uid },
        ],
        optional: {
          order: 'metadata.updatedAt',
          orderDirection: ESort.DESC,
          limit: params?.pageSize,
        },
      })) || [];

    return {
      [schedulerPath]: result.map(
        (doc: DocumentData) => _.merge({ id: doc.id }, doc) as IScheduler,
      ),
      total: result.length,
    };
  } catch (error) {
    logger.error('getSchedulers error', error);
    throw error;
  }
};
